import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import DownloadIcon from '@mui/icons-material/Download';

const SimplePdfViewer = ({ pdfUrl, height }) => {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [pageRendering, setPageRendering] = useState(false);
  const canvasRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Calculate responsive sizes
  const getResponsiveSizes = () => {
    if (isMobile) {
      return {
        buttonSize: 'small',
        iconSize: 'small',
        typography: {
          fontSize: '0.75rem', // 12px
          padding: '4px',
        },
        controls: {
          padding: '4px 4px',
        },
      };
    } else if (isTablet) {
      return {
        buttonSize: 'small',
        iconSize: 'medium',
        typography: {
          fontSize: '0.875rem', // 14px
          padding: '6px',
        },
        controls: {
          padding: '6px 12px',
        },
      };
    }
    return {
      buttonSize: 'small',
      iconSize: 'medium',
      typography: {
        fontSize: '1rem', // 16px
        padding: '8px',
      },
      controls: {
        padding: '8px 16px',
      },
    };
  };

  const responsiveSizes = getResponsiveSizes();
  // Load the PDF document
  useEffect(() => {
    let isMounted = true;

    const loadPDF = async () => {
      try {
        // Load PDF.js script dynamically
        if (!window.pdfjsLib) {
          const script = document.createElement('script');
          script.src =
            'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.min.js';
          script.async = true;

          await new Promise((resolve, reject) => {
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });

          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/build/pdf.worker.min.js';
        }

        // Load the PDF
        const loadingTask = window.pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;

        if (!isMounted) return;

        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setLoading(false);
      } catch (err) {
        console.error('Error loading PDF:', err);
        if (isMounted) {
          setError('Failed to load PDF. Please try again later.');
          setLoading(false);
        }
      }
    };

    loadPDF();

    return () => {
      isMounted = false;
    };
  }, [pdfUrl]);

  // Render the current page
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;

    let renderTask = null;

    const QUALITY_SCALE = 2.0; // Increase this for better quality

    // In the renderPage function, modify the scaling calculation:
    const renderPage = async () => {
      setPageRendering(true);

      try {
        const page = await pdfDoc.getPage(currentPage);

        // Get the parent container width
        const parentWidth = containerRef.current.clientWidth;

        // Calculate base scale to fit container width
        const viewport = page.getViewport({ scale: 1.0 });
        const baseScale = parentWidth / viewport.width;

        // Apply quality multiplier to the scale
        const finalScale = baseScale * QUALITY_SCALE;
        const scaledViewport = page.getViewport({ scale: finalScale });

        // Set canvas dimensions
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        // Set canvas dimensions to the scaled size
        canvas.height = scaledViewport.height;
        canvas.width = scaledViewport.width;

        // Set canvas CSS size to match container
        canvas.style.width = `${viewport.width * baseScale}px`;
        canvas.style.height = `${viewport.height * baseScale}px`;

        // Enable image smoothing
        context.imageSmoothingEnabled = true;
        context.imageSmoothingQuality = 'high';

        // Render with higher DPI
        const renderContext = {
          canvasContext: context,
          viewport: scaledViewport,
          enableWebGL: true,
          renderInteractiveForms: true,
        };

        renderTask = page.render(renderContext);
        await renderTask.promise;
        setPageRendering(false);
      } catch (err) {
        console.error('Error rendering page:', err);
        setPageRendering(false);
        setError('Failed to render page. Please try again.');
      }
    };

    renderPage();

    return () => {
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [pdfDoc, currentPage]);

  // Handle page navigation
  const goToPrevPage = () => {
    if (currentPage > 1 && !pageRendering) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < numPages && !pageRendering) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'hipaa_authorization.pdf'; // You can customize the download filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height: height || '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
      }}
    >
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" align="center" sx={{ p: 2 }}>
          {error}
        </Typography>
      )}

      <Box
        sx={{
          display: loading ? 'none' : 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
          height: '100%',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {/* Navigation and Download Controls */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: responsiveSizes.controls.padding,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
            flexWrap: 'nowrap',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={goToPrevPage}
              disabled={currentPage <= 1 || pageRendering}
              size={responsiveSizes.buttonSize}
            >
              <NavigateBeforeIcon fontSize={responsiveSizes.iconSize} />
            </IconButton>
            <Typography
              variant="caption"
              sx={{
                mx: 1,
                fontSize: responsiveSizes.typography.fontSize,
                padding: responsiveSizes.typography.padding,
              }}
            >
              Page {currentPage} of {numPages}
            </Typography>
            <IconButton
              onClick={goToNextPage}
              disabled={currentPage >= numPages || pageRendering}
              size={responsiveSizes.buttonSize}
            >
              <NavigateNextIcon fontSize={responsiveSizes.iconSize} />
            </IconButton>
          </Box>
          <DownloadIcon onClick={handleDownload} />
        </Box>

        {/* PDF Canvas Container */}
        <Box
          sx={{
            mt: isMobile ? '88px' : '56px', // Adjusted for mobile layout
            width: '100%',
          }}
        >
          <canvas
            ref={canvasRef}
            style={{
              maxWidth: '100%',
              height: 'auto',
              top: isMobile ? 30 : 40,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

SimplePdfViewer.propTypes = {
  pdfUrl: PropTypes.string.isRequired,
  height: PropTypes.string,
};

export default SimplePdfViewer;
